import React, { useState } from 'react';
import { ClockIcon, UserIcon } from './Icons';
import styles from '../styles/CoachCard.module.css';

/**
 * Coach Card Component
 * Displays coach profile cards for booking sessions
 *
 * Props:
 * - id: Coach ID
 * - displayName: Coach display name
 * - bio: Coach bio/description
 * - specializations: Array of specialization tags
 * - sessionDuration: Session duration in minutes
 * - profileImage: Profile image URL
 * - similarity: Optional RAG similarity score
 * - onSelect: Callback when coach is selected for booking
 */
function CoachCard({
  id,
  displayName,
  bio,
  specializations = [],
  sessionDuration = 60,
  profileImage,
  similarity,
  onSelect
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleSelectCoach = () => {
    if (onSelect) {
      onSelect({
        id,
        displayName,
        bio,
        specializations,
        sessionDuration,
        profileImage
      });
    }
  };

  // Format specializations for display
  const specializationList = Array.isArray(specializations)
    ? specializations
    : (specializations ? [specializations] : []);

  return (
    <div className={styles.coachCard}>
      <div className={styles.coachCardHeader}>
        Book a Session
      </div>

      <div className={styles.coachCardBody}>
        <div className={styles.coachProfile}>
          <div className={styles.coachImageContainer}>
            {profileImage && !imageError ? (
              <>
                {!imageLoaded && (
                  <div className={styles.imagePlaceholder}>
                    <div className={styles.imageLoader}></div>
                  </div>
                )}
                <img
                  src={profileImage}
                  alt={`${displayName}`}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{ display: imageLoaded ? 'block' : 'none' }}
                  className={styles.coachImage}
                />
              </>
            ) : (
              <div className={styles.coachImageFallback}>
                <UserIcon size={32} />
              </div>
            )}
          </div>

          <div className={styles.coachInfo}>
            <div className={styles.coachName}>{displayName}</div>
            <div className={styles.coachMeta}>
              <ClockIcon size={14} />
              <span>{sessionDuration} min session</span>
            </div>
          </div>
        </div>

        {bio && (
          <div className={styles.coachBio}>
            {bio.length > 150 ? `${bio.substring(0, 150)}...` : bio}
          </div>
        )}

        {specializationList.length > 0 && (
          <div className={styles.specializationTags}>
            {specializationList.slice(0, 3).map((spec, index) => (
              <span key={index} className={styles.specializationTag}>
                {spec}
              </span>
            ))}
            {specializationList.length > 3 && (
              <span className={styles.moreTag}>
                +{specializationList.length - 3} more
              </span>
            )}
          </div>
        )}

        <button
          className={styles.bookButton}
          onClick={handleSelectCoach}
        >
          View Available Times
        </button>
      </div>
    </div>
  );
}

export default CoachCard;
